import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Pencil, Trash2, MapPin, Factory, Fuel } from "lucide-react";


/**
 * Loading Sources — master data for the private-point suppliers and petrol
 * pumps the ops team sources fuel from. The Assign-Loading dialog picks from
 * this list so vendor names stay consistent across reports and drivers don't
 * have to retype them each time.
 */
const SOURCE_LABELS = { private_point: "Private Point", petrol_pump: "Petrol Pump" };
const SOURCE_ICONS = { private_point: Factory, petrol_pump: Fuel };


export default function LoadingSources() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["loading-sources", "all"],
    queryFn: () => api.get("/loading-sources?include_inactive=true").then((r) => r.data.items || []),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/loading-sources/${id}`),
    onSuccess: () => { toast.success("Source deactivated"); qc.invalidateQueries({ queryKey: ["loading-sources"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="loading-sources-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight">Loading Sources</h1>
          <p className="text-sm text-muted-foreground">
            Saved private-point suppliers and petrol pumps · used by the Assign-Loading dropdown
          </p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-source">
          <Plus size={16} className="mr-1" /> New source
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>All sources</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data || []).length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No sources yet. Click <b>New source</b> to add your first supplier or pump.
            </div>
          )}
          {(data || []).map((s) => {
            const Icon = SOURCE_ICONS[s.source_type] || Factory;
            return (
              <div key={s.id} className="grid grid-cols-1 gap-3 rounded-lg border border-border/60 p-4 md:grid-cols-12" data-testid={`source-${s.code}`}>
                <div className="md:col-span-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon size={18} />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Name · Type</div>
                  <div className="font-bold">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{SOURCE_LABELS[s.source_type]}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Address</div>
                  <div className="text-sm">{s.address || "—"}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contact</div>
                  <div className="text-sm">{s.contact_person || "—"}</div>
                  <div className="text-xs text-muted-foreground">{s.phone || ""}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Density</div>
                  <div className="text-sm">{s.default_density_kg_per_litre ? `${s.default_density_kg_per_litre} kg/L` : "—"}</div>
                  {!s.is_active && (
                    <div className="mt-0.5 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground w-fit">Inactive</div>
                  )}
                </div>
                <div className="md:col-span-1 flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`btn-edit-${s.code}`}>
                    <Pencil size={14} />
                  </Button>
                  {s.is_active && (
                    <Button size="sm" variant="ghost" className="text-status-cancelled hover:bg-status-cancelled/10"
                            onClick={() => window.confirm(`Deactivate ${s.name}?`) && delMut.mutate(s.id)}
                            data-testid={`btn-del-${s.code}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <SourceDialog
        open={open}
        source={editing}
        onClose={() => setOpen(false)}
        onDone={() => { qc.invalidateQueries({ queryKey: ["loading-sources"] }); setOpen(false); }}
      />
    </div>
  );
}


function SourceDialog({ open, source, onClose, onDone }) {
  const isEdit = !!source;
  const initial = {
    name: "", source_type: "private_point",
    address: "", contact_person: "", phone: "",
    default_density_kg_per_litre: 0.832, notes: "", is_active: true,
  };
  const [form, setForm] = useState(initial);

  React.useEffect(() => {
    if (open) {
      setForm(source ? {
        name: source.name || "",
        source_type: source.source_type || "private_point",
        address: source.address || "",
        contact_person: source.contact_person || "",
        phone: source.phone || "",
        default_density_kg_per_litre: source.default_density_kg_per_litre || 0,
        notes: source.notes || "",
        is_active: source.is_active !== false,
      } : initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source]);

  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mut = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        default_density_kg_per_litre: form.default_density_kg_per_litre || null,
      };
      return isEdit
        ? api.patch(`/loading-sources/${source.id}`, payload)
        : api.post("/loading-sources", payload);
    },
    onSuccess: () => { toast.success(isEdit ? "Source updated" : "Source created"); onDone?.(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="source-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${source?.code}` : "New loading source"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Type</Label>
            <Select value={form.source_type} onValueChange={(v) => setVal("source_type", v)}>
              <SelectTrigger data-testid="source-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private_point">Private Point (weighbridge)</SelectItem>
                <SelectItem value="petrol_pump">Petrol Pump (retail)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Name <span className="text-status-cancelled">*</span></Label>
            <Input value={form.name} onChange={(e) => setVal("name", e.target.value)}
                   placeholder={form.source_type === "petrol_pump" ? "e.g. HP Camp Chowk" : "e.g. BPCL Chakan Depot"}
                   data-testid="source-name" />
          </div>
          <div className="col-span-2">
            <Label>Address <MapPin size={12} className="inline" /></Label>
            <Input value={form.address} onChange={(e) => setVal("address", e.target.value)} data-testid="source-address" />
          </div>
          <div>
            <Label>Contact person</Label>
            <Input value={form.contact_person} onChange={(e) => setVal("contact_person", e.target.value)} data-testid="source-contact" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setVal("phone", e.target.value)} data-testid="source-phone" />
          </div>
          {form.source_type === "private_point" && (
            <div className="col-span-2">
              <Label>Default density (kg/L)</Label>
              <Input type="number" step="0.001" value={form.default_density_kg_per_litre || ""}
                     onChange={(e) => setVal("default_density_kg_per_litre", Number(e.target.value))}
                     data-testid="source-density" placeholder="e.g. 0.832" />
              <div className="mt-1 text-xs text-muted-foreground">
                Pre-fills the Assign dialog when a dispatcher picks this supplier.
              </div>
            </div>
          )}
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setVal("notes", e.target.value)} />
          </div>
          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => setVal("is_active", v)} data-testid="source-active" />
            <span className="text-sm">Active</span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => mut.mutate()} disabled={!form.name.trim() || mut.isPending} data-testid="btn-save-source">
            {mut.isPending ? "Saving…" : (isEdit ? "Update source" : "Create source")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
